import React, { useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import RefreshIcon from "@material-ui/icons/Refresh";
import Tooltip from "@material-ui/core/Tooltip";
import { ThreeDots } from "react-loader-spinner";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import useThemeColors from "../../hooks/useThemeColors";
import { useTranslation } from "react-i18next";
import ModalHeader from "components/ModalHeader";
import useChat from "../../hooks/useChat";
import { useAIChatFilters } from "../../contexts/AIChatFiltersContext";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import ArtifactPanel from "./ArtifactPanel";

const useStyles = makeStyles(() => ({
  dialogPaper: {
    width: "75vw",
    height: "75vh",
    maxWidth: "none",
    maxHeight: "none",
    margin: "auto",
    borderRadius: "12px",
    overflow: "hidden",
  },
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    flexShrink: 0,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  headerIcon: {
    fontSize: "20px",
  },
  headerTitle: {
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  headerSubtitle: {
    fontSize: "12px",
    color: "#888",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    marginLeft: "8px",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  body: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  chatPanel: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
  },
  chatPanelFull: {
    flex: 1,
  },
  chatPanelSplit: {
    flex: "0 0 50%",
    maxWidth: "50%",
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "16px",
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": {
      scrollbarColor: grayColor[6] + " transparent",
    },
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "3px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      background: grayColor[6],
    },
  },
  artifactPanel: {
    flex: "0 0 50%",
    maxWidth: "50%",
    overflow: "hidden",
  },
  welcomeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: "24px",
    textAlign: "center",
  },
  welcomeIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    opacity: 0.7,
  },
  welcomeTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "8px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  welcomeSubtitle: {
    fontSize: "14px",
    color: "#777",
    marginBottom: "24px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    maxWidth: "400px",
    lineHeight: "1.5",
  },
  suggestionsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    maxWidth: "420px",
  },
  suggestionButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 16px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#fff",
    cursor: "pointer",
    textAlign: "center",
    fontSize: "13.5px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    color: "#444",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "#bbb",
      backgroundColor: "#f8f8f8",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    },
  },
  thinkingContainer: {
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: "16px",
  },
  thinkingBubble: {
    backgroundColor: "#f5f5f5",
    borderRadius: "12px",
    borderTopLeftRadius: "4px",
    padding: "10px 14px",
    maxWidth: "85%",
  },
  thinkingStep: {
    fontSize: "13px",
    color: "#888",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  disclaimer: {
    padding: "8px 14px",
    margin: "0 0 6px",
    backgroundColor: "#f5f5f5",
    fontSize: "12px",
    lineHeight: 1.45,
    color: "#888",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontStyle: "italic",
    textAlign: "center",
    flexShrink: 0,
  },
  evalToggle: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    marginRight: "4px",
  },
  evalToggleLabel: {
    fontSize: "11px",
    color: "rgba(255,255,255,0.72)",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  evalSwitch: {
    transform: "scale(0.7)",
    marginRight: "-4px",
  },
}));

export default function AIChatModal({ open, onClose, isDev }) {
  const classes = useStyles();
  const { clientColor } = useThemeColors();
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);

  const suggestions = useMemo(() => {
    const raw = t("ai_chat.suggestions", { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw.map((text, i) => ({ id: `s${i + 1}`, text }));
  }, [t]);

  const {
    messages,
    isLoading,
    thinkingSteps,
    selectedArtifact,
    streamingContent,
    sendMessage,
    stopStreaming,
    selectArtifact,
    clearArtifact,
    resetChat,
  } = useChat();

  const { pageName } = useAIChatFilters();
  const prevPageNameRef = useRef(pageName);
  useEffect(() => {
    if (
      prevPageNameRef.current !== null &&
      pageName !== null &&
      prevPageNameRef.current !== pageName
    ) {
      resetChat();
    }
    prevPageNameRef.current = pageName;
  }, [pageName, resetChat]);

  const [evaluationMode, setEvaluationMode] = useState(false);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, thinkingSteps, streamingContent]);

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion.text);
  };

  const hasMessages = messages.length > 0;
  const showArtifact = selectedArtifact !== null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.root}>
        <ModalHeader
          title={t("ai_chat.modal_title", {
            defaultValue: "Profit InSights AI Assistant",
          })}
          subtitleParts={[
            t("ai_chat.welcome_title", {
              defaultValue: "How can I help you?",
            }),
            <strong key="beta">
              {t("ai_chat.modal_subtitle", { defaultValue: "Beta" })}
            </strong>,
          ]}
          rightContent={
            <div className={classes.headerActions}>
              {isDev && (
                <Tooltip title="Toggle evaluation mode (AI vs Human scoring)">
                  <div className={classes.evalToggle}>
                    <span className={classes.evalToggleLabel}>Eval</span>
                    <Switch
                      checked={evaluationMode}
                      onChange={() => setEvaluationMode((prev) => !prev)}
                      size="small"
                      className={classes.evalSwitch}
                      color="primary"
                    />
                  </div>
                </Tooltip>
              )}
              {hasMessages && (
                <Tooltip
                  title={t("ai_chat.new_conversation", {
                    defaultValue: "New conversation",
                  })}
                >
                  <IconButton
                    size="small"
                    onClick={resetChat}
                    style={{ color: "#fff" }}
                  >
                    <RefreshIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          }
          onClose={onClose}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />

        {/* Body */}
        <div className={classes.body}>
          {/* Chat panel */}
          <div
            className={`${classes.chatPanel} ${showArtifact ? classes.chatPanelSplit : classes.chatPanelFull}`}
          >
            <div className={classes.messagesArea}>
              {!hasMessages && !isLoading ? (
                <div className={classes.welcomeContainer}>
                  <div className={classes.welcomeIcon}>✦</div>
                  <div
                    className={classes.welcomeTitle}
                    style={{ color: clientColor }}
                  >
                    {t("ai_chat.welcome_title", {
                      defaultValue: "How can I help you?",
                    })}
                  </div>
                  <div className={classes.welcomeSubtitle}>
                    {t("ai_chat.welcome_subtitle", {
                      defaultValue:
                        "I can analyze your P&L results, compare periods, explain business rules and more.",
                    })}
                  </div>
                  <div className={classes.suggestionsGrid}>
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        className={classes.suggestionButton}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion.text}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => (
                    <ChatMessage
                      key={msg.id}
                      message={msg}
                      messageIndex={idx}
                      messages={messages}
                      onSelectArtifact={selectArtifact}
                      isDev={isDev}
                      evaluationMode={evaluationMode}
                    />
                  ))}

                  {isLoading && streamingContent && (
                    <ChatMessage
                      message={{
                        id: "streaming",
                        role: "assistant",
                        content: streamingContent,
                        artifacts: [],
                      }}
                      onSelectArtifact={selectArtifact}
                    />
                  )}

                  {isLoading &&
                    !streamingContent &&
                    thinkingSteps.length > 0 && (
                      <div className={classes.thinkingContainer}>
                        <div className={classes.thinkingBubble}>
                          <div className={classes.thinkingStep}>
                            {thinkingSteps[thinkingSteps.length - 1]}
                          </div>
                          <ThreeDots
                            color={grayColor[3]}
                            height="10px"
                            width="28px"
                            wrapperStyle={{ marginTop: "4px" }}
                          />
                        </div>
                      </div>
                    )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {!hasMessages && !isLoading && (
              <div className={classes.disclaimer}>
                {t("ai_chat.disclaimer")}
              </div>
            )}

            <ChatInput
              onSend={sendMessage}
              onStop={stopStreaming}
              disabled={isLoading}
              autoFocus={open}
            />
          </div>

          {/* Artifact panel */}
          {showArtifact && (
            <div className={classes.artifactPanel}>
              <ArtifactPanel
                artifact={selectedArtifact}
                onClose={clearArtifact}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

AIChatModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isDev: PropTypes.bool,
};
